import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tripsApi, transactionsApi, peopleApi } from '@/services/api';
import { formatDate, CATEGORY_MAP } from '@tally/shared';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ArrowLeft, MapPin, Calendar, Users, Download, Plus, X, Check, Pencil } from 'lucide-react';
import { TransactionTable } from '@/components/shared/TransactionTable';
import { ROUTES } from '@/routes';
import { useState } from 'react';
import { formatUsd, localSummary } from '@/utils/money';

export function TripDetailPage() {
  const { tripId } = useParams<{ tripId: string }>();

  const { data: tripData, isLoading: tripLoading } = useQuery({
    queryKey: ['trip', tripId],
    queryFn: () => tripsApi.get(tripId!),
    enabled: !!tripId,
  });

  const { data: txData } = useQuery({
    queryKey: ['trip-transactions', tripId],
    queryFn: () => transactionsApi.list({ tripId: tripId!, pageSize: '200' }),
    enabled: !!tripId,
  });

  if (tripLoading) {
    return <div className="h-96 animate-pulse rounded-xl bg-muted" />;
  }

  const trip = tripData?.data;
  const transactions = txData?.data?.items || [];
  if (!trip) return <p>Trip not found</p>;

  const categoryData = (trip.expensesByCategory || []).map((c: any) => ({
    name: CATEGORY_MAP.get(c.category)?.label || c.category,
    value: c.total,
    color: CATEGORY_MAP.get(c.category)?.color || '#94a3b8',
    count: c.count,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to={ROUTES.app.trips} className="rounded-lg p-2 hover:bg-accent">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <TripTitle tripId={trip.id} name={trip.name} status={trip.status} />
          <div className="mt-1 flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{trip.destination?.city}{trip.destination?.country ? `, ${trip.destination.country}` : ''}</span>
            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{formatDate(trip.startDate, 'long')} — {formatDate(trip.endDate, 'long')}</span>
            {trip.travelers?.length > 0 && (
              <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{trip.travelers.length} traveler{trip.travelers.length > 1 ? 's' : ''}</span>
            )}
          </div>
        </div>
        <a
          href={transactionsApi.export({ tripId: tripId!, format: 'csv' })}
          className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          <Download className="h-4 w-4" /> Export
        </a>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5">
          <p className="text-xs text-muted-foreground">Total spent (USD)</p>
          <p className="mt-1 text-2xl font-bold">{formatUsd(trip.totalExpenses)}</p>
          {localSummary(trip.totalsByCurrency, 3) && <p className="mt-1 text-xs text-muted-foreground">{localSummary(trip.totalsByCurrency, 3)}</p>}
        </div>
        {trip.perPersonCost?.length > 0 && trip.perPersonCost.slice(0, 2).map((p: any) => (
          <div key={p.travelerId} className="rounded-xl border border-border bg-card p-5">
            <p className="text-xs text-muted-foreground">{p.travelerName} ({p.travelerType})</p>
            <p className="mt-1 text-2xl font-bold">{formatUsd(p.totalCost)}</p>
          </div>
        ))}
      </div>

      {trip.stops?.length > 0 && <TripStops stops={trip.stops} />}

      <TripTravelers tripId={trip.id} travelers={trip.travelers || []} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="mb-4 text-sm font-semibold">Expenses by Category</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categoryData} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v)}`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={75} />
                <Tooltip formatter={(value: number) => formatUsd(value)} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {categoryData.map((entry: any, i: number) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="mb-4 text-sm font-semibold">Category Split</h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={categoryData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value" paddingAngle={2}>
                  {categoryData.map((entry: any, i: number) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatUsd(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-1">
            {categoryData.slice(0, 6).map((c: any) => (
              <div key={c.name} className="flex items-center gap-1.5 text-xs">
                <div className="h-2 w-2 rounded-full" style={{ backgroundColor: c.color }} />
                <span className="truncate text-muted-foreground">{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <h3 className="mb-4 text-sm font-semibold">Transactions ({transactions.length})</h3>
        <TransactionTable
          transactions={transactions}
          showTripColumn={false}
          showSearch={true}
          pageSize={20}
          compact={true}
        />
      </div>
    </div>
  );
}

function TripTitle({ tripId, name, status }: { tripId: string; name: string; status: string }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => tripsApi.update(tripId, body),
    onSuccess: () => {
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ['trip', tripId] });
      queryClient.invalidateQueries({ queryKey: ['trips'] });
    },
  });

  return (
    <div className="flex flex-wrap items-center gap-2">
      {editing ? (
        <form onSubmit={(e) => { e.preventDefault(); if (draft.trim()) update.mutate({ name: draft.trim() }); }} className="flex gap-2">
          <input value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus className="rounded-lg border border-input bg-background px-3 py-1.5 text-lg font-bold" />
          <button type="submit" className="rounded-lg bg-primary px-3 text-sm text-primary-foreground">Save</button>
          <button type="button" onClick={() => setEditing(false)} className="text-sm text-muted-foreground">Cancel</button>
        </form>
      ) : (
        <>
          <h1 className="text-2xl font-bold">{name}</h1>
          <button onClick={() => { setDraft(name); setEditing(true); }} className="rounded p-1 text-muted-foreground hover:bg-accent" aria-label="Rename trip">
            <Pencil className="h-4 w-4" />
          </button>
        </>
      )}
      {status === 'detected' ? (
        <button onClick={() => update.mutate({ status: 'confirmed' })} className="flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 text-xs hover:bg-accent">
          <Check className="h-3 w-3" /> Confirm trip
        </button>
      ) : (
        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-950 dark:text-blue-400">{status.replace('_', ' ')}</span>
      )}
    </div>
  );
}

function TripStops({ stops }: { stops: Array<{ city: string | null; country: string | null; countryCode: string | null; from: string; to: string }> }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <h3 className="mb-4 text-sm font-semibold">Stops</h3>
      <ol className="relative space-y-3 border-l border-border pl-5">
        {stops.map((s, i) => (
          <li key={i}>
            <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border-2 border-card bg-primary" />
            <p className="text-sm font-medium">{[s.city, s.country || s.countryCode?.toUpperCase()].filter(Boolean).join(', ')}</p>
            <p className="text-xs text-muted-foreground">
              {formatDate(s.from)}{s.to !== s.from ? ` — ${formatDate(s.to)}` : ''}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}

interface Traveler {
  id: string;
  name: string;
  type: 'adult' | 'child' | 'infant';
  isFromHousehold: boolean;
  householdMemberId: string | null;
}

function TripTravelers({ tripId, travelers }: { tripId: string; travelers: Traveler[] }) {
  const queryClient = useQueryClient();
  const { data: peopleData } = useQuery({ queryKey: ['people'], queryFn: () => peopleApi.list() });
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<Traveler['type']>('adult');

  const save = useMutation({
    mutationFn: (next: Traveler[]) => tripsApi.update(tripId, { travelers: next }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trip', tripId] });
      queryClient.invalidateQueries({ queryKey: ['trips'] });
    },
  });

  const people = peopleData?.data ?? [];
  const notOnTrip = people.filter((p) => !travelers.some((t) => t.householdMemberId === p.id));

  const addPerson = (p: (typeof people)[number]) =>
    save.mutate([...travelers, { id: p.id, name: p.name, type: p.ageGroup, isFromHousehold: true, householdMemberId: p.id }]);
  const addAdHoc = () => {
    if (!newName.trim()) return;
    save.mutate([...travelers, { id: `adhoc-${Date.now()}`, name: newName.trim(), type: newType, isFromHousehold: false, householdMemberId: null }]);
    setNewName('');
    setShowAdd(false);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Travelers ({travelers.length})</h3>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent"
        >
          <Plus className="h-3 w-3" /> Add traveler
        </button>
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        {travelers.map((t) => (
          <div key={t.id} className="flex items-center gap-2 rounded-full border border-border bg-accent/50 px-3 py-1.5">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">{t.name.charAt(0)}</div>
            <span className="text-sm">{t.name}</span>
            <span className="text-xs text-muted-foreground">({t.type})</span>
            <button
              onClick={() => save.mutate(travelers.filter((x) => x.id !== t.id))}
              className="ml-1 rounded-full p-0.5 hover:bg-destructive/10 hover:text-destructive"
              aria-label={`Remove ${t.name}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>

      {showAdd && (
        <div className="space-y-3 rounded-lg border border-dashed border-border p-4">
          {notOnTrip.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium text-muted-foreground">Your people:</p>
              <div className="flex flex-wrap gap-2">
                {notOnTrip.map((p) => (
                  <button key={p.id} onClick={() => addPerson(p)} className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs hover:bg-accent">
                    <Plus className="h-3 w-3" /> {p.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground">Someone else:</p>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Name (e.g. friend, colleague)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addAdHoc()}
                className="flex-1 rounded-lg border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <select value={newType} onChange={(e) => setNewType(e.target.value as Traveler['type'])} className="rounded-lg border border-input bg-background px-2 py-1.5 text-sm">
                <option value="adult">Adult</option>
                <option value="child">Child</option>
                <option value="infant">Infant</option>
              </select>
              <button onClick={addAdHoc} disabled={!newName.trim()} className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40">
                Add
              </button>
            </div>
          </div>
        </div>
      )}
      {save.error && <p className="text-sm text-destructive">{(save.error as Error).message}</p>}
    </div>
  );
}
